import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import ExploreViewType from '@alltrails/amplitude/enums/ExploreViewType';
import type ListItem from '@alltrails/lists/types/ListItem';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AllTrailsBounds, AllTrailsCenter, LngLat } from '@alltrails/maps/types/Geo';

type PaddingOptions = ReturnType<NonNullable<ReturnType<typeof useMap>>['getPadding']>;

export type ListToast = { type: 'added'; itemType: ListItem['type']; objectId: number } | { type: 'removed'; item: ListItem } | null;

export type ExploreState = {
  currentTrail?: { id: number; slug: string };
  conditionsPanelOpen: boolean;
  exploreViewType?: ExploreViewType;
  headerHeight?: number;
  initialMapBounds?: AllTrailsBounds;
  initialMapCenter?: AllTrailsCenter;
  isMobileListAtTop: boolean;
  isPrintRequested: boolean;
  isShowingMobileList: boolean;
  listPageInfo: { listId: number; isOwner: boolean; isEditable: boolean; listName: string } | null;
  listPageItems: ListItem[] | null;
  listSyncVersion: number;
  listToast: ListToast;
  mapPadding?: PaddingOptions;
  hasNavigatedClientSide: boolean;
  isReturningToExplore: boolean;
  panelCollapsed: boolean;
  returningBounds?: AllTrailsBounds;
  showFilterBar?: boolean;
  showRecenterButton?: boolean;
  streetAddressLngLat?: LngLat;
  visibleMapBounds?: AllTrailsBounds;
};

export const initialState: ExploreState = {
  conditionsPanelOpen: false,
  hasNavigatedClientSide: false,
  isReturningToExplore: false,
  isMobileListAtTop: true,
  isPrintRequested: false,
  isShowingMobileList: false,
  listPageInfo: null,
  listPageItems: null,
  listSyncVersion: 0,
  listToast: null,
  panelCollapsed: false
};

export const exploreSlice = createSlice({
  name: 'explore',
  initialState,
  reducers: {
    clearPrintRequest: (state: ExploreState) => {
      state.isPrintRequested = false;
    },
    requestPrint: (state: ExploreState) => {
      state.isPrintRequested = true;
    },
    resetState: (state: ExploreState, action: PayloadAction<{ persistKeys?: (keyof ExploreState)[]; initialState?: Partial<ExploreState> }>) => {
      const defaultState: ExploreState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (defaultState[key] as typeof persistValue) = persistValue;
      });

      return defaultState;
    },
    setConditionsPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.conditionsPanelOpen = action.payload;
    },
    setCurrentExploreTrail: (state, action: PayloadAction<{ id: number; slug: string } | undefined>) => {
      state.currentTrail = action.payload;
    },
    setExploreViewType: (state, action: { payload: ExploreViewType | undefined }) => {
      state.exploreViewType = action.payload;
    },
    setHeaderHeight: (state, action: { payload: number }) => {
      state.headerHeight = action.payload;
    },
    setInitialMapBounds: (state, action: { payload: AllTrailsBounds }) => {
      state.initialMapBounds = action.payload;
    },
    setInitialMapCenter: (state, action: { payload: AllTrailsCenter }) => {
      state.initialMapCenter = action.payload;
    },
    setIsMobileListAtTop: (state, action: { payload: boolean }) => {
      state.isMobileListAtTop = action.payload;
    },
    setIsShowingMobileList: (state, action: { payload: boolean }) => {
      state.isShowingMobileList = action.payload;
    },
    setMapPadding: (state, action: { payload: PaddingOptions }) => {
      state.mapPadding = action.payload;
    },
    setPanelCollapsed: (state, action: { payload: boolean }) => {
      state.panelCollapsed = action.payload;
    },
    setHasNavigatedClientSide: (state, action: { payload: boolean }) => {
      state.hasNavigatedClientSide = action.payload;
    },
    setIsReturningToExplore: (state, action: { payload: boolean }) => {
      state.isReturningToExplore = action.payload;
    },
    setReturningBounds: (state, action: { payload: AllTrailsBounds | undefined }) => {
      state.returningBounds = action.payload;
    },
    setShowRecenterButton: (state, action: { payload: boolean }) => {
      state.showRecenterButton = action.payload;
    },
    setStreetAddressLngLat: (state, action: { payload: LngLat | undefined }) => {
      state.streetAddressLngLat = action.payload;
    },
    setListPageInfo: (state, action: PayloadAction<{ listId: number; isOwner: boolean; isEditable: boolean; listName: string } | null>) => {
      state.listPageInfo = action.payload;
    },
    setListPageEditable: (state, action: PayloadAction<boolean>) => {
      if (state.listPageInfo) {
        state.listPageInfo.isEditable = action.payload;
      }
    },
    setListPageItems: (state, action: PayloadAction<ListItem[]>) => {
      state.listPageItems = action.payload;
    },
    addListPageItem: (state, action: PayloadAction<ListItem>) => {
      if (!state.listPageItems) {
        state.listPageItems = [action.payload];
        return;
      }
      const exists = state.listPageItems.some(i => i.type === action.payload.type && i.objectId === action.payload.objectId);
      if (!exists) {
        state.listPageItems.push(action.payload);
      }
    },
    upsertListPageItem: (state, action: PayloadAction<ListItem>) => {
      if (!state.listPageItems) {
        state.listPageItems = [action.payload];
        return;
      }
      const idx = state.listPageItems.findIndex(i => i.type === action.payload.type && i.objectId === action.payload.objectId);
      if (idx !== -1) {
        state.listPageItems[idx] = action.payload;
      } else {
        state.listPageItems.unshift(action.payload);
      }
    },
    removeListPageItem: (state, action: PayloadAction<{ type: ListItem['type']; objectId: number }>) => {
      if (!state.listPageItems) {
        return;
      }
      const idx = state.listPageItems.findIndex(i => i.type === action.payload.type && i.objectId === action.payload.objectId);
      if (idx !== -1) {
        state.listPageItems.splice(idx, 1);
      }
    },
    setListToast: (state, action: PayloadAction<ListToast>) => {
      state.listToast = action.payload;
    },
    requestListSync: state => {
      state.listSyncVersion += 1;
    },
    setVisibleMapBounds: (state, action: { payload: AllTrailsBounds | undefined }) => {
      state.visibleMapBounds = action.payload;
    }
  }
});

export const {
  clearPrintRequest,
  requestPrint,
  resetState,
  setExploreViewType,
  setConditionsPanelOpen,
  setCurrentExploreTrail,
  setListPageInfo,
  setListPageEditable,
  setHeaderHeight,
  setInitialMapBounds,
  setInitialMapCenter,
  setIsMobileListAtTop,
  setIsShowingMobileList,
  setListPageItems,
  addListPageItem,
  upsertListPageItem,
  removeListPageItem,
  setListToast,
  requestListSync,
  setMapPadding,
  setPanelCollapsed,
  setHasNavigatedClientSide,
  setIsReturningToExplore,
  setReturningBounds,
  setShowRecenterButton,
  setStreetAddressLngLat,
  setVisibleMapBounds
} = exploreSlice.actions;
export default exploreSlice.reducer;
